import {
	ConfigureVariableSettingsRequest,
	DefineVariableRequest,
	FormElementVariableType,
	ProcessBuilderPage,
	Variable,
} from '@/scripts/types/generated';
import {Page} from '@inertiajs/core';
import {
	sendCreateRequest,
	sendDeleteRequest,
	sendReloadRequest,
	sendUpdateRequest,
} from '@/scripts/utils/crud-requests.ts';
import {FieldFormElementType} from '@/scripts/generated/form-element-type.ts';

const variables = ref<Variable[]>();
const isLoading = ref(false);

export const useVariables = () => {
	const refreshVariables = (page: Page<ProcessBuilderPage>) => {
		variables.value = page.props.variables;
	};

	const getVariableType = (
		type: FieldFormElementType | 'list'
	): FormElementVariableType => {
		if (type === 'checkbox' || type === 'radio' || type === 'select') {
			return 'selectable';
		}

		if (type === 'file-upload') {
			return 'file';
		}

		if (type === 'color-picker') {
			return 'color';
		}

		return type;
	};

	const initVariables = async () => {
		if (isLoading.value || variables.value) return;

		isLoading.value = true;
		const page = await sendReloadRequest<ProcessBuilderPage>(['variables']);
		refreshVariables(page);
		isLoading.value = false;
	};
	const createVariable = async (
		request: DefineVariableRequest
	): Promise<Variable> => {
		const page = await sendCreateRequest<ProcessBuilderPage>(
			route('tenant.variables.store'),
			request,
			['variables']
		);

		return new Promise<Variable>((resolve) => {
			refreshVariables(page);
			const newVariable = variables.value.find(
				(variable) =>
					variable.name === request.name &&
					variable.parentUuid === request.parentUuid
			);
			resolve(newVariable);
		});
	};

	const updateVariableSettings = async (
		versionUuid: string,
		request: ConfigureVariableSettingsRequest
	): Promise<Variable> => {
		const page = await sendUpdateRequest<ProcessBuilderPage>(
			route('tenant.variables.settings.update', {
				variableVersion: versionUuid,
			}),
			request,
			['variables']
		);

		return new Promise<Variable>((resolve) => {
			refreshVariables(page);
			const newVariable = variables.value.find(
				(variable) => variable.versionUuid === versionUuid
			);
			resolve(newVariable);
		});
	};

	const deleteVariable = async (uuid: string): Promise<void> => {
		const page = await sendDeleteRequest<ProcessBuilderPage>(
			route('tenant.variables.destroy', {
				variable: uuid,
			}),
			['variables']
		);

		return new Promise<void>((resolve) => {
			refreshVariables(page);
			resolve();
		});
	};

	const getVariable = async (versionUuid: string): Promise<Variable> => {
		if (!variables.value) {
			await initVariables();
			// throw new Error(`Variable not yet initialized`);
		}

		return variables.value.find(
			(variable) => variable.versionUuid === versionUuid
		);
	};

	const setVariables = (newVariables: Variable[]) => {
		variables.value = newVariables;
	};

	return {
		initVariables,
		isLoading,
		createVariable,
		updateVariableSettings,
		deleteVariable,
		getVariable,
		getVariableType,
		variables,
		setVariables,
	};
};
